import React from 'react'
import styled, {keyframes} from 'styled-components'

import {Box, Text, Dropdown, sx, SxProp, BoxProps, themeGet} from '../..'

import {
  DropdownProps,
  DropdownTriggerProps,
  DropdownItemProps,
  DropdownContentProps,
  DropdownPortalProps
} from '../../components/Dropdown'

import {AkChevronDownIcon, AkMoreIcon} from '@fuegokit/fuegoicons-react'

// --------------------------------------------------------
// Rule Builder Header Menu
// @TODO: combine with GlobalHeaderMenu and abstract into an Atlassian specific Dropdown
export interface RuleBuilderHeaderMenuProps extends DropdownProps {
  /**
   * Content to render in the dropdown
   */
  children?: React.ReactNode
}
/**
 * Displays a dropdown menu that can be triggered with a button and that allows a user to make a single selection.
 */
const RuleBuilderHeaderMenu = ({children, ...props}: RuleBuilderHeaderMenuProps) => {
  return <StyledDropdown>{children}</StyledDropdown>
}
const StyledDropdown = styled(Dropdown)``

const slideUpAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`
const slideRightAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateX(-2px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`
const slideDownAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(-2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`
const slideLeftAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateX(2px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`
export interface MenuItemProps extends DropdownItemProps {
  /**
   *
   */
  description?: string
  /**
   *
   */
  href?: string
}
/**
 * Displays a dropdown menu item. Should be rendered inside a RuleBuilderHeader Menu.
 */
const Item = ({children, disabled, description, onSelect, href, sx, ...props}: MenuItemProps & SxProp) => {
  return (
    <StyledMenuItem disabled={disabled} onSelect={onSelect} href={href} sx={sx} {...props}>
      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
        {children}
        <>
          {description ? (
            <Box as="span">
              <Text
                as="p"
                color={disabled ? 'text.disabled' : 'text.subtlest'}
                sx={{fontSize: 1, marginTop: 1, wordBreak: 'break-word'}}
              >
                {description}
              </Text>
            </Box>
          ) : null}
        </>
      </Box>
    </StyledMenuItem>
  )
}
const StyledMenuItem = styled(Dropdown.Item)<MenuItemProps & SxProp>`
  font-size: ${themeGet('fontSizes.3')};
  line-height: 1;
  min-height: 40px;
  color: ${themeGet('colors.text.default')};
  display: flex;
  align-items: center;

  height: ${themeGet('space.5')};
  padding: 0 ${themeGet('space.2')};
  position: relative;
  padding-left: ${themeGet('space.3')};
  user-select: none;
  outline: none;
  text-align: left;

  margin: 0;
  text-decoration: none;

  &[data-highlighted] {
    color: ${themeGet('colors.text.default')};
    background-color: ${themeGet('colors.background.neutral.subtle.hovered')};
    cursor: pointer;
  }
  &[data-disabled] {
    color: ${themeGet('colors.text.disabled')};
  }
  &:hover {
    text-decoration: none;
    background-color: ${themeGet('colors.background.neutral.subtle.hovered')};
  }
  &:active {
    background-color: ${themeGet('colors.background.selected.default.[default]')};
    color: ${themeGet('colors.text.selected')};
  }
  &:visited {
    color: ${themeGet('colors.text.selected')};
  }
  ${sx}
`
/**
 * The dropdown menu trigger. Must be rendered inside a RuleBuilderHeader Menu.
 */
const Trigger = ({children, ...props}: DropdownTriggerProps) => {
  return <StyledMenuTrigger>{children}</StyledMenuTrigger>
}
const StyledMenuTrigger = styled(Dropdown.Trigger)<DropdownTriggerProps & SxProp>`
  // reset
  appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  user-select: none;
  display: flex;
  align-items: center;
  color: inherit;
  font-weight: normal;
  font-size: ${themeGet('fontSizes.3')};
  text-decoration: none;
  opacity: 0.85;
  font-weight: ${themeGet('fontWeights.medium')};
  padding: ${themeGet('space.2')} 12px;
  border-radius: ${themeGet('radii.1')};
  &:hover,
  &:focus {
    opacity: 1;
    text-decoration: none;
    color: ${themeGet('colors.link.default')};
    background-color: ${themeGet('colors.background.neutral.subtle.hovered')};
  }
  &:active {
    opacity: 1;
    text-decoration: none;
    background-color: ${themeGet('colors.background.selected.default.[default]')};
    color: ${themeGet('colors.text.selected')};
  }
  &:focus,
  .focus-visible {
    outline: none;
    transition-duration: 0s, 0.2s;
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  &:focus:not(:focus-visible):not(.focus-visible) {
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  svg {
    transition: transform 0.15s ease;
  }
  &[data-state='open'] {
    /* --icon-rotation: 90deg; */
    background-color: ${themeGet('colors.background.selected.default.[default]')};
    color: ${themeGet('colors.text.selected')};
    /* svg {
      transform: rotate(var(--icon-rotation));
    } */
  }
  ${sx}
`
/**
 * The dropdown menu content that pops out when the dropdown menu is open.
 */
const Content = ({children, ...props}: DropdownContentProps) => {
  return <StyledMenuContent {...props}>{children}</StyledMenuContent>
}
const StyledMenuContent = styled(Dropdown.Content)`
  min-width: 220px;
  max-width: 800px;
  max-height: calc(100vh - 16px);
  position: absolute;
  right: -24px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  border: 1px solid ${themeGet('colors.border.default')};
  border-radius: ${themeGet('radii.2')};
  padding: ${themeGet('space.2')} 0;
  box-shadow: ${themeGet('shadows.elevation.shadow.overlay')};
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
  &[data-side='top'] {
    animation-name: ${slideDownAndFade};
  }
  &[data-side='right'] {
    animation-name: ${slideLeftAndFade};
  }
  &[data-side='bottom'] {
    animation-name: ${slideUpAndFade};
  }
  &[data-side='left'] {
    animation-name: ${slideRightAndFade};
  }
`
const Portal = ({children, ...props}: DropdownPortalProps) => {
  return <StyledMenuPortal {...props}>{children}</StyledMenuPortal>
}
const StyledMenuPortal = styled(Dropdown.Portal)``

/**
 * The separator
 */
const Separator = ({children, ...props}: DropdownPortalProps) => {
  return <StyledMenuSeparator {...props}>{children}</StyledMenuSeparator>
}
const StyledMenuSeparator = styled(Dropdown.Separator)`
  height: 1px;
  background-color: ${themeGet('colors.border.default')};
  margin: ${themeGet('space.2')};
`

RuleBuilderHeaderMenu.displayName = 'RulebuilderHeaderMenu'
Item.displayName = 'RuleBuilderHeaderMenu.Item'
Trigger.displayName = 'RuleBuilderHeaderMenu.Trigger'
Portal.displayName = 'RuleBuilderHeaderMenu.Portal'
Separator.displayName = 'RuleBuilderHeaderMenu.Separator'

export default Object.assign(RuleBuilderHeaderMenu, {
  Trigger,
  Portal,
  Item,
  Content,
  Separator
})
